// Iter 387 — Regularización de anticipos (clientes y proveedores): bandeja + detalle por auxiliar + aplicación manual.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ArrowLeftRight, HandCoins, Wallet, RefreshCw, Sparkles, FileText, Ban, Search } from 'lucide-react';
import { toast } from 'sonner';
import { AuxiliaresTable } from './regularizacion/AuxiliaresTable';
import { AuxiliarWorkbench } from './regularizacion/AuxiliarWorkbench';
import { NoAnticiposTab } from './regularizacion/NoAnticiposTab';
import { SincronizarAlegraButton } from './regularizacion/SincronizarAlegraButton';
import { fmtCLP, LADO_INFO } from './regularizacion/regularizacionUtils';

function Kpi({ icon: Icon, color, label, value, sub, testId }) {
  return (
    <Card data-testid={testId}>
      <CardContent className="p-3 flex items-center gap-3">
        <div className={`p-2 rounded-lg ${color.bg}`}><Icon className={`h-4 w-4 ${color.fg}`} /></div>
        <div className="min-w-0">
          <p className="text-[11px] text-muted-foreground">{label}</p>
          <p className={`text-xl font-bold tabular-nums ${color.text || ''}`}>{value}</p>
          {sub && <p className="text-[10px] text-muted-foreground truncate">{sub}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

export default function RegularizacionAnticiposPage() {
  const { user } = useAuth();
  const puedeAplicar = ['admin', 'gerencia', 'contabilidad'].includes(user?.rol);
  const [params, setParams] = useSearchParams();
  const lado = params.get('lado') === 'ingresos' ? 'ingresos' : 'egresos';
  const vista = params.get('vista') === 'no-anticipos' ? 'no-anticipos' : 'pendientes';
  const rutSel = params.get('rut') || '';
  const [data, setData] = useState({ auxiliares: [], kpis: {} });
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [syncTick, setSyncTick] = useState(0);
  const info = LADO_INFO[lado];

  const setParam = useCallback((k, v) => setParams((p) => { const n = new URLSearchParams(p); if (v) n.set(k, v); else n.delete(k); return n; }), [setParams]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/contabilidad/regularizacion/resumen', { params: { lado, desde: desde || undefined, hasta: hasta || undefined } });
      setData(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo cargar la bandeja');
    } finally {
      setLoading(false);
    }
  }, [lado, desde, hasta]);
  useEffect(() => { if (!rutSel) load(); }, [load, rutSel]);

  const auxiliares = useMemo(() => {
    const t = q.trim().toLowerCase().replace(/\./g, '');
    return t ? data.auxiliares.filter((a) => `${a.nombre} ${a.rut}`.toLowerCase().includes(t)) : data.auxiliares;
  }, [data.auxiliares, q]);
  const k = data.kpis || {};

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="regularizacion-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl md:text-2xl font-bold flex items-center gap-2"><ArrowLeftRight className="w-6 h-6 text-indigo-600" /> Regularización de Anticipos</h1>
          <p className="text-sm text-muted-foreground">Cruza manualmente anticipos y documentos pendientes cuando la aplicación automática no alcanza. Cada aplicación actualiza Alegra (pago/cobro + consumo del anticipo).</p>
        </div>
        <div className="flex items-start gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="btn-recargar"><RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Actualizar</Button>
          <SincronizarAlegraButton puedeAplicar={puedeAplicar} onDone={() => { if (!rutSel) load(); setSyncTick((t) => t + 1); }} />
        </div>
      </div>

      {/* 1. Clasificación inicial */}
      <Tabs value={lado} onValueChange={(v) => { setParams((p) => { const n = new URLSearchParams(p); n.set('lado', v); n.delete('rut'); n.delete('vista'); return n; }); }}>
        <TabsList className="h-auto p-1" data-testid="tabs-lado">
          <TabsTrigger value="ingresos" className="px-4 py-2 flex flex-col items-start gap-0 data-[state=active]:bg-emerald-600 data-[state=active]:text-white" data-testid="tab-ingresos">
            <span className="flex items-center gap-1.5 font-semibold"><HandCoins className="w-4 h-4" /> {LADO_INFO.ingresos.titulo}</span>
            <span className="text-[10px] opacity-80 font-normal">{LADO_INFO.ingresos.sub}</span>
          </TabsTrigger>
          <TabsTrigger value="egresos" className="px-4 py-2 flex flex-col items-start gap-0 data-[state=active]:bg-rose-600 data-[state=active]:text-white" data-testid="tab-egresos">
            <span className="flex items-center gap-1.5 font-semibold"><Wallet className="w-4 h-4" /> {LADO_INFO.egresos.titulo}</span>
            <span className="text-[10px] opacity-80 font-normal">{LADO_INFO.egresos.sub}</span>
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {rutSel ? (
        <AuxiliarWorkbench key={`${rutSel}-${syncTick}`} lado={lado} rut={rutSel} puedeAplicar={puedeAplicar} onVolver={() => setParam('rut', '')} />
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Kpi icon={FileText} color={{ bg: 'bg-indigo-100', fg: 'text-indigo-700' }} label={`${info.auxiliarPlural.charAt(0).toUpperCase() + info.auxiliarPlural.slice(1)} con pendientes`} value={k.auxiliares ?? '—'} testId="kpi-auxiliares" />
            <Kpi icon={Wallet} color={{ bg: 'bg-emerald-100', fg: 'text-emerald-700', text: 'text-emerald-700' }} label="Anticipos pendientes" value={fmtCLP(k.saldo_anticipos || 0)} sub={`${k.anticipos || 0} movimientos`} testId="kpi-anticipos" />
            <Kpi icon={FileText} color={{ bg: 'bg-rose-100', fg: 'text-rose-700', text: 'text-rose-700' }} label={`Documentos por ${info.accionDoc}`} value={fmtCLP(k.saldo_documentos || 0)} sub={`${k.documentos || 0} documentos`} testId="kpi-documentos" />
            <Kpi icon={Sparkles} color={{ bg: 'bg-violet-100', fg: 'text-violet-700' }} label="Con sugerencias" value={k.con_sugerencias ?? '—'} sub="monto similar y ≤ 20 días" testId="kpi-sugerencias" />
            <Kpi icon={Ban} color={{ bg: 'bg-amber-100', fg: 'text-amber-700' }} label="No son anticipos" value={k.no_anticipos ?? '—'} sub={k.sin_verificar ? `${k.sin_verificar} sin verificar cuenta` : fmtCLP(k.monto_no_anticipos || 0)} testId="kpi-no-anticipos" />
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <Button size="sm" variant={vista === 'pendientes' ? 'default' : 'outline'} onClick={() => setParam('vista', '')} data-testid="vista-pendientes">Pendientes de regularizar ({k.auxiliares || 0})</Button>
            {lado === 'egresos' && <Button size="sm" variant={vista === 'no-anticipos' ? 'default' : 'outline'} onClick={() => setParam('vista', 'no-anticipos')} data-testid="vista-no-anticipos"><Ban className="w-3.5 h-3.5 mr-1" /> No son anticipos ({k.no_anticipos || 0})</Button>}
          </div>

          {vista === 'no-anticipos' ? <NoAnticiposTab lado={lado} puedeAplicar={puedeAplicar} onCambio={load} /> : (
            <Card>
              <CardContent className="p-0">
                <div className="p-3 border-b flex items-end gap-3 flex-wrap" data-testid="filtros">
                  <div className="flex-1 min-w-[240px]">
                    <Label className="text-[11px]">Buscar {info.auxiliar.toLowerCase()} (nombre o RUT)</Label>
                    <div className="relative"><Search className="w-4 h-4 absolute left-2 top-2.5 text-muted-foreground" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ej: Prodalam o 76..." className="pl-8 h-9" data-testid="input-buscar" /></div>
                  </div>
                  <div><Label className="text-[11px]">Anticipos desde</Label><Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="h-9 w-40" data-testid="input-desde" /></div>
                  <div><Label className="text-[11px]">Hasta</Label><Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className="h-9 w-40" data-testid="input-hasta" /></div>
                  {(q || desde || hasta) && <Button variant="ghost" size="sm" onClick={() => { setQ(''); setDesde(''); setHasta(''); }} data-testid="btn-limpiar-filtros">Limpiar</Button>}
                </div>
                <AuxiliaresTable lado={lado} auxiliares={auxiliares} loading={loading} onAbrir={(a) => setParam('rut', a.rut)} />
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
